// Shrink the Manpower page to the width of a sheet of paper before the print
// dialog opens.
//
// ── Why this is not pure CSS ─────────────────────────────────────────────────
// The crew grid's width is DATA, not layout: four label columns plus one column
// per day or per week, so a 4-week daily view is 32 columns and a 2-week view is
// 18. Measured on the real grid: 919px at 2 weeks, 1229px at 4. No fixed font
// size fits both onto ~960px of landscape paper — one setting either wastes half
// the sheet or runs off it, and the failure is silent because the browser simply
// clips whatever crosses the edge.
//
// So the scale is measured at print time and applied as `zoom`, which is the one
// property that both shrinks and REFLOWS: `transform: scale()` would render the
// page at a different size while paginating as though it had not, cutting rows
// at the wrong places. zoom is layout-affecting in Chromium and Firefox, which
// is the whole browser estate here.

/**
 * Printable width in CSS pixels: landscape Letter (11in) less the 10mm margins
 * in the `@page` rule, at 96dpi. A4 landscape is wider (11.69in), so sizing to
 * Letter fits both.
 */
const PRINTABLE_PX = 960;

/**
 * Never shrink past this. Below roughly 45% the 11px grid type lands under 5px
 * and the sheet is worthless — better to let a genuinely enormous range clip and
 * be obviously wrong than to hand somebody an unreadable page that looks fine
 * from a distance.
 */
const MIN_SCALE = 0.45;

/**
 * How wide the page WOULD be on paper.
 *
 * ⚠️ Measured under `mp-measuring`, which applies the print stylesheet's own
 * compact sizing and pins the page to the paper width. Measuring the screen
 * instead gets both surfaces wrong, in opposite directions: the crew grid reads
 * narrower than it prints, and the Gap Analysis tables read far WIDER, because
 * on screen they carry a `min-width` of 1560px that exists only to keep columns
 * legible while scrolling. Against that screen figure the fit came out at 0.615
 * — shrinking 9.5px print type to under 6px to solve an overflow that the print
 * rules had already solved by releasing the min-width. Now the same tables
 * measure exactly the page width and take no zoom at all.
 */
function widthOnPaper(root: ParentNode): number {
  document.documentElement.classList.add('mp-measuring');
  // Reading a layout property flushes the pending style change synchronously,
  // so the measurements below are taken under the class rather than before it.
  void document.documentElement.offsetWidth;

  let widest = 0;
  root.querySelectorAll<HTMLElement>('.mp-fit').forEach((el) => {
    widest = Math.max(widest, el.scrollWidth);
  });

  document.documentElement.classList.remove('mp-measuring');
  return widest;
}

/** Print the page, scaled so every column lands on the sheet. */
export function printFitted(): void {
  const page = document.querySelector<HTMLElement>('.mp-page');
  if (!page) { window.print(); return; }

  const widest = widthOnPaper(page);
  const scale = widest > PRINTABLE_PX
    ? Math.max(MIN_SCALE, PRINTABLE_PX / widest)
    : 1;

  page.style.setProperty('--mp-print-zoom', String(scale));

  // Cleared on afterprint so the screen never keeps the print scale. The
  // listener is one-shot: a second print re-measures, because the range or the
  // filters may have changed in between.
  const reset = () => {
    page.style.removeProperty('--mp-print-zoom');
    window.removeEventListener('afterprint', reset);
  };
  window.addEventListener('afterprint', reset);

  window.print();
}
